import { ArticleBlock, paragraphs } from '../types/article'

export const storyPartOne: ArticleBlock[] = [
  ...paragraphs(
    `My dog Bear got out again last October.`,
    `Third time in two months.`,
    `He's a husky.`,
    `If you know huskies, you already know what that means.`,
    `I was standing barefoot in the driveway at 10 PM, screaming his name into the dark.`,
    `My neighbor across the street opened her door and just looked at me.`,
    `She didn't say anything.`,
    `She didn't have to.`,
    `I already felt like **the worst dog owner on the street**.`,
    `I found him twenty minutes later, two blocks away, sniffing around a dumpster behind the gas station.`,
    `He was fine.`,
    `But my hands were shaking the whole drive home.`,
    `Because the only thing I could think while I was looking for him was the thing I think every single time he gets out.`,
  ),
  { kind: 'quote', text: `What if this is the time he doesn't come back?` },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/7643f34d-6582-4f5c-8b04-1e9095dcf4f7.jpg',
    alt: 'An open weathered backyard gate at dusk with a leash hanging on the post, looking out to a dark empty suburban street',
    caption:
      'This is the view I still check first. An open gate, a leash on the hook, and no idea how long he has been gone.',
  },
  ...paragraphs(
    `If you're reading this, you probably know exactly what that feels like.`,
    `That sick feeling in your stomach when you realize the gate is open.`,
    `The way your heart drops when you call his name and he doesn't come.`,
    `The panic when you realize he could be anywhere — the neighbor's yard, the park, the road.`,
    `I lived with that feeling for almost two years.`,
    `And I want to tell you what I learned — because I wish someone had told me sooner.`,
  ),
  {
    kind: 'heading',
    text: `I Blamed Myself for Two Years. Then My Vet Told Me I Had It Backwards.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/5cRuwdPUN8VvoXa1mcRp9j/hf_20260826_110936_afba1906-2ef2-4ec5-a941-866e3cf09fbf.webp',
    alt: 'Split image: a husky running across open snow beside a husky staring out through the gaps of a backyard fence',
  },
  ...paragraphs(
    `For the longest time, I thought it was my fault.`,
    `I thought I wasn't training Bear well enough.`,
    `I thought I wasn't watching him close enough.`,
    `I thought there was something wrong with him.`,
    `My husband thought so too.`,
    `He kept saying if I'd just be more consistent with the training, Bear would stop.`,
    `I tried everything.`,
    `I bought books.`,
    `I watched videos.`,
    `I stood in the yard for hours correcting him every time he went near the fence.`,
    `And then a squirrel would cross the yard and two years of training disappeared in three seconds.`,
    `My vet told me something that completely changed how I saw the problem.`,
    `She said Bear wasn't escaping because he was bad.`,
    `He was escaping because he was doing **exactly what his breed was built to do**.`,
    `Huskies were bred to run a hundred miles a day pulling sleds through snow.`,
    `Beagles were bred to follow a scent for hours.`,
    `German Shepherds were bred to patrol property lines bigger than most neighborhoods.`,
    `Labs were bred to retrieve across open fields and rivers.`,
    `And we put these dogs in a backyard and expect them to just sit there.`,
    `Some of them dig.`,
    `Some of them climb.`,
    `Some of them wait by the gate and bolt the second someone opens it.`,
    `Bear?`,
    `He figured out that if he threw his weight against the bottom corner of the fence, it flexed just enough for him to squeeze through.`,
    `He did it three times before I even figured out how he was getting out.`,
    `That's not a bad dog.`,
    `That's a husky being a husky.`,
    `And once I understood that, I stopped blaming myself.`,
    `But I still didn't have a solution.`,
  ),
  {
    kind: 'heading',
    text: `She Found Her Lab Beside the Highway the Next Morning. Not a Scratch. Just Gone.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/rFrZ1HVdP4ik95xPyTWkiB/hf_20260826_110942_18d2f567-1c7a-4814-affb-b862297934f7.webp',
    alt: 'An empty leather dog collar with a name tag lying on the centre line of an empty highway at dusk',
    caption:
      'A dog that reaches a highway does not understand what a car is. This is the part of the story nobody wants to read.',
  },
  ...paragraphs(
    `Here's what nobody tells you when you have an escape-prone dog.`,
    `Every time they get out, there's a clock running.`,
    `And you don't know how much time is on it.`,
    `A dog that follows a scent trail can be a mile away in minutes.`,
    `A dog that bolts after a squirrel doesn't hear you calling.`,
    `A dog that reaches a highway doesn't understand what a car is.`,
    `I started reading forums and Facebook groups for people with escape-prone dogs.`,
    `I wish I hadn't.`,
    `One woman posted about her chocolate lab who ran through her invisible fence and ended up on the highway.`,
    `She found him the next day, lying beside the road.`,
    `No visible injuries.`,
    `Just gone.`,
    `She said he was her family's first baby.`,
    `A year later her daughter was born — and grew up never knowing him.`,
    `I read dozens of stories like that one.`,
    `Dogs that got out and never came home.`,
    `Dogs that were found on the side of the road.`,
    `Dogs whose owners never got the chance to say goodbye.`,
    `These stories destroyed me.`,
    `Because I kept seeing Bear in every one of them.`,
    `There was one night — and I've never told anyone this — where I lay in bed and thought: what happens to Bear if I can't fix this? What if he gets out and I'm not fast enough? What if he gets hurt so badly that I have to — I couldn't even finish the thought.`,
    `But it was there.`,
    `And it scared me more than any escape ever had.`,
    `That was the night I stopped hoping the problem would go away on its own.`,
    `I stopped trying to manage it. I needed to actually fix it.`,
    `And the first thing I needed to know was how bad it really was.`,
  ),
  {
    kind: 'heading',
    text: `I Checked Five of These. My Dog Was in Danger Every Single Day.`,
  },
  ...paragraphs(
    `When I finally talked to someone who actually helped me, the first thing she asked was whether my dog showed any of these signs.`,
  ),
  {
    kind: 'checklist',
    items: [
      `He has escaped before — even once`,
      `He digs along the fence line or scratches at gates`,
      `He rushes the door every time it opens`,
      `He chases squirrels, rabbits, or other animals and completely ignores your voice`,
      `He is a husky, beagle, lab, German shepherd, border collie, or boxer`,
      `Your yard has no fence, a partial fence, or a fence under six feet`,
      `You live near a road or a busy street`,
      `You have gaps under your fence, a gate that doesn't latch right, or a yard that backs up to open land`,
    ],
  },
  ...paragraphs(
    `She told me if I checked **even one**, my dog was at risk every single day I didn't do something about it.`,
    `I checked five.`,
    `And that's when I started seriously looking at every option I could find.`,
  ),
  {
    kind: 'heading',
    text: `$6,200 for a Fence. And It Still Would Not Have Stopped My Dog.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/31hE182ZgGWnsXq3r3zA72/image.png',
    alt: 'A new professionally installed cedar fence with clawed, splintered boards broken outward and a "Premier Fence Co. $6,200" contractor sign beside the gap',
    caption:
      'Six thousand dollars of professionally installed cedar. A determined dog goes through the weakest panel anyway.',
  },
  ...paragraphs(
    `Trust me when I say I tried everything.`,
    `First I called a fence company.`,
    `They quoted me over $6,000.`,
    `For a basic wood fence around my yard, professionally installed, with a four-to-six-week wait.`,
    `My HOA had restrictions on fence height, so even if I could have afforded it, I couldn't have built it tall enough to stop a husky.`,
    `You know what a husky does with a six-foot fence?`,
    `He climbs it.`,
    `My husband kept saying we should just save up for a real fence.`,
    `He said it was the only thing that would actually work.`,
    `He's a good man.`,
    `But he wasn't the one standing in the driveway at 10 PM screaming Bear's name.`,
    `I didn't have a year to save up.`,
    `I needed something now.`,
    `Then I looked at buried-wire invisible fences.`,
    `Those run thousands installed.`,
    `But I read too many stories about dogs who figured out they could sprint through the shock area before it kicked in.`,
    `One guy on a dog forum said he buried the wire in an S-curve so his Boxer had to run through three separate shock areas to get out.`,
    `His dog figured it out in a week.`,
    `Then I looked at the GPS collars.`,
    `Halo was the first one I found.`,
    `It looked great until I saw the price — over $600 — and then I saw that you had to pay a monthly subscription just to turn on the fence feature.`,
    `I'm sorry, but no.`,
    `I'm not paying a monthly fee for the rest of my dog's life just to keep him in my own yard.`,
    `Then I found the cheaper GPS collars on Amazon and Walmart.`,
    `But the reviews scared me.`,
    `People said the GPS drifted 20 feet.`,
    `People said the signal dropped under tree cover.`,
    `People said they waited weeks for the collar to ship, and when it didn't work, the company only offered a partial refund — after they fought for it.`,
    `One person said they couldn't even test the collar without opening the packaging, and the company used that as a reason to deny the return.`,
    `So my options were:`,
  ),
  {
    kind: 'callout',
    title: `Every option I had, and why each one failed`,
    items: [
      `A fence I couldn't afford and my HOA wouldn't allow.`,
      `A buried wire system my dog would learn to run through.`,
      `A $600+ GPS collar with a monthly fee I resented on principle.`,
      `Or a cheap collar I couldn't trust with Bear's life.`,
    ],
  },
  ...paragraphs(
    `I was stuck.`,
    `I honestly started to think there was no answer.`,
  ),
]
